// src/components/AgentTitleBar.js
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import './AgentTitleBar.css';
import { ShineBorder } from "./ShineBorder";


// Existing icons
import { ReactComponent as ChainIcon } from '../../assets/agents/chain.svg';
import { ReactComponent as NewsIcon } from '../../assets/agents/news.svg';
import { ReactComponent as QuantIcon } from '../../assets/agents/quant.svg';
import { ReactComponent as VesterIcon } from '../../assets/agents/vester.svg';
import { ReactComponent as RiskIcon } from '../../assets/agents/risk.svg';
import { ReactComponent as WalletIcon } from '../../assets/agents/wallet.svg';

// Agent colors
export const AGENT_COLORS = {
  vester: [
    "rgba(18, 157, 232, 0.8)",     // base
    "rgba(80, 190, 255, 0.8)",     // tint
    "rgba(10, 120, 200, 0.8)"      // shade
  ],
  quant: [
    "rgba(34, 197, 94, 0.8)",
    "rgba(80, 230, 140, 0.8)",
    "rgba(20, 150, 70, 0.8)"
  ],
  news: [
    "rgba(255, 79, 94, 0.8)",
    "rgba(255, 120, 130, 0.8)",
    "rgba(200, 40, 50, 0.8)"
  ],
  onchain: [
    "rgba(255, 169, 40, 0.8)",
    "rgba(255, 190, 90, 0.8)",
    "rgba(200, 135, 30, 0.8)"
  ],
  portfolio: [
    "rgba(40, 120, 232, 0.8)",
    "rgba(90, 160, 255, 0.8)",
    "rgba(20, 90, 200, 0.8)"
  ],
  risk: [
    "rgba(232, 212, 40, 0.8)",
    "rgba(255, 235, 90, 0.8)",
    "rgba(200, 185, 20, 0.8)"
  ],
};

const AGENT_COLORS_BACKGROUND = {
  vester: "rgba(18, 157, 232)",
  quant: "rgba(34, 197, 94)",
  news: "rgba(255, 79, 94)",
  onchain: "rgba(255, 169, 40)",
  portfolio: "rgba(40, 120, 232)",
  risk: "rgba(232, 212, 40)",
};

// Optional: tiny wordmark-less “Vester” renders as text only
const VESTER_INFO = {
  title: 'Vester — Orchestrated Research',
  content: (
    <>
      <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
        Vester — Orchestrated Research
      </Typography>
      <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
        Smart router that chooses and coordinates agents for you. It fuses outputs into a single,
        concise brief with visuals and clear action items.
      </Typography>
    </>
  ),
};

const AGENT_INFO = {
  vester: {
    ...VESTER_INFO,
    sampleQueries: [
      'Analyze Bitcoin market trends for the next quarter',
      'What are the top DeFi opportunities right now?',
      'Compare Ethereum and Solana fundamentals',
    ],
  },
  onchain: {
    title: 'On-Chain Analyst — Flow & Behavior',
    content: (
      <>
        <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
          On-Chain Analyst — Flow &amp; Behavior
        </Typography>
        <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
          Tracks whale wallets, exchange flows, liquidity shifts, and cohort behavior to connect
          on-chain activity to likely market impact.
        </Typography>
      </>
    ),
    sampleQueries: [
      'Show me recent whale movements for Bitcoin',
      'Analyze exchange inflows for major tokens',
      'Track liquidity shifts across top DEXs',
    ],
  },
  news: {
    title: 'News Agent — Market Pulse',
    content: (
      <>
        <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
          News Agent — Market Pulse
        </Typography>
        <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
          Scans headlines and social feeds for catalysts, narrative shifts, and sentiment drivers.
        </Typography>
      </>
    ),
  },
  quant: {
    title: 'The Quant — Forecasting & Technicals',
    content: (
      <>
        <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
          The Quant — Forecasting &amp; Technicals
        </Typography>
        <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
          Blends predictive models with technician-style analysis and delivers crisp visuals.
        </Typography>
      </>
    ),
  },
  portfolio: {
    title: 'Portfolio Manager — Positions & Allocation',
    content: (
      <>
        <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
          Portfolio Manager — Positions &amp; Allocation
        </Typography>
        <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
          Tracks holdings, P/L, allocation drift, and rebalancing opportunities. Surfaces top movers
          and suggests actions aligned with your risk profile.
        </Typography>
      </>
    ),
  },
  risk: {
    title: 'Risk Manager — Drawdown & Exposure',
    content: (
      <>
        <Typography component="div" sx={{ fontWeight: 500, mb: 1, fontSize: '16px' }}>
          Risk Manager — Drawdown &amp; Exposure
        </Typography>
        <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.85)' }}>
          Monitors volatility, VaR, tail risk, and correlation spikes. Flags concentration, liquidity
          risks, and scenario stress results with clear mitigations.
        </Typography>
      </>
    ),
  },
};

const AgentTitleBar = ({
  agentType,
  displayName,
  onAgentSelect,
  sx = {},
  ...props
}) => {
  const agentInfo = AGENT_INFO[agentType] || { title: displayName, content: displayName };
  const color = AGENT_COLORS[agentType] || ['#A5A5A5'];

  const renderIcon = () => {
    const commonProps = { width: 20, height: 20 };
    switch (agentType) {
      case 'onchain':
        return <ChainIcon {...commonProps} style={{ stroke: 'rgba(0,0,0,0.85)' }} />;
      case 'news':
        return <NewsIcon {...commonProps} style={{ stroke: 'rgba(0,0,0,0.85)' }} />;
      case 'quant':
        return <QuantIcon {...commonProps} style={{ fill: 'rgba(0,0,0,0.85)' }} />;
      case 'portfolio':
        return <WalletIcon {...commonProps} style={{ stroke: 'rgba(0,0,0,0.85)' }} />;
      case 'risk':
        return <RiskIcon {...commonProps} style={{ fill: 'rgba(0,0,0,0.85)' }} />;
      case 'vester':
        return <VesterIcon {...commonProps} style={{ fill: 'rgba(0,0,0,0.85)' }} />;
      default:
        return null;
    }
  };

  const handleClick = () => {
    if (onAgentSelect) {
      onAgentSelect(agentType);
    }
  };

  return (
    <Tooltip
      title={agentInfo.content}
      arrow={false}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(255,255,255,0.92)',
            color: '#000',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            fontSize: 14,
            border: '1px solid rgba(200,200,200,0.6)',
            borderRadius: '10px',
            p: 1.5,
            boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
            maxWidth: 360,
          },
        },
      }}
    >
      <Box sx={{ display: 'inline-flex', 
        alignItems: 'center', 
        gap: 0.75, 
        cursor: 'default', 
        borderRadius: '12px', 
        position: 'relative', 
        padding: '4px 16px 4px 16px', 
        fontFamily: 'InterTight', 
        fontSize: '12px',
        height: '32px',
        background: '#ffffff',
        border: 'none',
        ...sx}}>
        <ShineBorder
          borderWidth={1}
          duration={3}
          shineColor={color}
          className="shine-border"
        />
        {renderIcon()}
        {displayName}
      </Box>
    </Tooltip>
  );
};

export default AgentTitleBar;
